import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import TrackedCTA from '@/components/TrackedCTA';
import { TRIAL_DAYS } from '@/config/tierPricing';
import { ErrorSummary } from '@/components/signup/FieldError';
import SignupField from '@/components/signup/SignupField';
import OrderSummary from '@/components/signup/OrderSummary';

const scrollToSubmit = () => {
  document.querySelector('[data-testid="signup-submit-btn"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const EmailExistsLink = ({ error }) => (error && /already has an account/.test(error) ? (
  <Link to="/login" className="text-xs text-primary hover:underline" data-testid="email-exists-login-link">Go to sign in →</Link>
) : null);

export default function CredentialsStep({
  accountType, tier, units, formData, errors = {}, showPassword, loading,
  onChange, onBlur, onToggleShowPassword, onBack, onSubmit, submitLabel
}) {
  const isFreelancer = accountType === 'freelancer';
  const pwType = showPassword ? 'text' : 'password';
  const common = { onChange, onBlur };

  return (
    <>
      <CardHeader>
        <Button variant="ghost" size="sm" onClick={onBack} className="w-fit -ml-2 mb-2" data-testid="credentials-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <CardTitle className="text-xl">Create Your Account</CardTitle>
        <CardDescription>Set up your login credentials</CardDescription>
      </CardHeader>
      <CardContent>
        {/* A/B-tested reassurance card at the highest signup drop-off step. */}
        <div className="mb-4">
          <TrackedCTA
            surface="signup_recovery"
            ctaId="credentials_reassurance"
            title="You're seconds away"
            icon={ShieldCheck}
            defaultDescription={`Free ${TRIAL_DAYS}-day trial. Cancel anytime. We never share your safety data — your team's signals stay encrypted and yours alone.`}
            defaultCtaLabel={isFreelancer ? 'Create my account' : 'Continue'}
            variantB={{
              description: "Join 140+ teams already using SAFER-i™. Your free trial starts the moment you click — no credit card required.",
              cta_label: "Start my free trial",
            }}
            onCtaClick={scrollToSubmit}
            testIdPrefix="signup-recovery"
          />
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <ErrorSummary errors={errors} testId="credentials-error-summary" />

          <SignupField id="email" label="Email Address *" type="email" value={formData.email} {...common}
            placeholder="you@company.com" autoComplete="email" error={errors.email} testId="email-input"
            hint={null} />
          <EmailExistsLink error={errors.email} />

          <SignupField id="password" label="Password *" type={pwType} value={formData.password} {...common}
            placeholder="Min. 8 characters" autoComplete="new-password" error={errors.password} testId="password-input"
            hint={formData.password.length >= 8 ? <p className="text-xs text-green-600" data-testid="password-ok-hint">Looks good</p> : null} />

          <SignupField id="confirmPassword" label="Confirm Password *" type={pwType} value={formData.confirmPassword} {...common}
            placeholder="Confirm your password" autoComplete="new-password" error={errors.confirmPassword}
            testId="confirm-password-input" />

          <label htmlFor="showPassword" className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" id="showPassword" checked={showPassword} onChange={onToggleShowPassword}
              className="rounded" data-testid="show-password-toggle" />
            Show password
          </label>

          <OrderSummary isFreelancer={isFreelancer} tier={tier} units={units} />

          <div className="text-xs text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-primary hover:underline" data-testid="signup-terms-link">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-primary hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
            {loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</>
              : <>{submitLabel || 'Create Account'} <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </form>
      </CardContent>
    </>
  );
}
